import { useState, useMemo, useRef, useEffect } from 'react';
import { useChildStore } from '../../store';
import { useFamilyStore } from '../../stores/familyStore';
import { useCoinStore } from '../../store/coinStore';
import { useAuthStore } from '../../store/authStore';
import { getLevelForXP, getXPToNextLevel } from '../../engine/xp-calculator';
import { AvatarWithAccessories } from '../AvatarWithAccessories';
import { Star, ChevronDown, Coins, User, LogOut, CreditCard } from 'lucide-react';
import { NotificationBell } from '../gamification/NotificationBell';
import { useShowRewards, useShowStreaks } from '../../hooks/useShowRewards';
import { isBetaFeatureEnabled } from '../../config/beta-surface';
import { VIRTUAL_REWARD_ECONOMY_ENABLED } from '../../config/rewards';

export function TopBar() {
  const { children, activeChildId } = useChildStore();
  // Render order comes from familyStore (overlay over childStore). The
  // authoritative Child records still live in childStore; we just look
  // them up in the order familyStore.childIds specifies. setActiveChild
  // goes through familyStore, which mirrors the write back to childStore.
  const childIds = useFamilyStore(s => s.family.childIds);
  const setActiveChild = useFamilyStore(s => s.setActiveChild);
  const childById = useMemo(
    () => new Map(children.map(c => [c.id, c])),
    [children]
  );
  const orderedChildren = useMemo(
    () => childIds.map(id => childById.get(id)).filter((c): c is NonNullable<typeof c> => !!c),
    [childIds, childById]
  );
  const child = children.find(c => c.id === activeChildId);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore(s => s.user);
  const profile = useAuthStore(s => s.profile);
  const signOut = useAuthStore(s => s.signOut);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  const coinBalance = useCoinStore(s => child ? (s.balances[child.id] || 0) : 0);
  const childEquippedRaw = useCoinStore(s => child ? s.equippedItems[child.id] : undefined);
  const childEquipped = useMemo(() => childEquippedRaw || [], [childEquippedRaw]);
  const showRewards = useShowRewards();
  const showStreaks = useShowStreaks();
  const showBilling = isBetaFeatureEnabled('billing');

  if (!child) return null;

  const level = getLevelForXP(child.xp);
  const { current, next, progress } = getXPToNextLevel(child.xp);

  const otherChildren = orderedChildren.filter(c => c.id !== activeChildId);

  return (
    <div
      className="backdrop-blur-xl border-b px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3 sticky top-0 z-30 shadow-md"
      style={{
        backgroundColor: 'var(--color-theme-topbar-bg)',
        borderColor: 'var(--color-theme-topbar-border)',
      }}
    >
      {/* Avatar + Name */}
      <div className="flex items-center gap-2 relative">
        <AvatarWithAccessories avatarEmoji={child.avatarEmoji} equippedItemIds={childEquipped} />
        <div>
          <p className="font-extrabold text-sm leading-tight" style={{ color: 'var(--color-theme-text-primary, #1f2937)', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{child.name}</p>
          {showRewards && (
            <p className="text-xs font-medium" style={{ color: 'var(--color-theme-text-secondary)', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
              Lvl {level.level} {level.title}
            </p>
          )}
        </div>
        {otherChildren.length > 0 && (
          <button
            onClick={() => setShowSwitcher(!showSwitcher)}
            className="ml-1 hover:opacity-80"
            style={{ color: 'var(--color-theme-text-secondary)' }}
            aria-label={`Switch child profile, currently ${child.name}`}
            aria-expanded={showSwitcher}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        )}

        {/* Child Switcher Dropdown */}
        {showSwitcher && otherChildren.length > 0 && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowSwitcher(false)} />
            <div
              className="absolute top-full left-0 mt-2 rounded-xl shadow-lg py-1 z-50 min-w-[160px]"
              style={{
                backgroundColor: 'var(--color-theme-surface-bg)',
                borderColor: 'var(--color-theme-surface-border)',
                border: '1px solid var(--color-theme-surface-border)',
                backdropFilter: 'blur(12px)',
              }}
            >
              {otherChildren.map(c => (
                <button
                  key={c.id}
                  onClick={() => { setActiveChild(c.id); setShowSwitcher(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:opacity-80 text-left"
                >
                  <span className="text-xl">{c.avatarEmoji}</span>
                  <span className="text-sm font-medium">{c.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* XP Bar */}
      {showRewards ? (
        <div className="flex-1 max-w-xs">
          <div className="flex items-center gap-1.5">
            <Star className="w-4 h-4 text-amber-500 fill-amber-400" aria-hidden="true" />
            <div
              className="flex-1 rounded-full h-3 overflow-hidden"
              style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
              role="progressbar"
              aria-label="Experience points progress"
              aria-valuenow={current}
              aria-valuemin={0}
              aria-valuemax={next}
            >
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${progress * 100}%`,
                  background: `linear-gradient(to right, var(--color-theme-xp-from), var(--color-theme-xp-to))`,
                }}
              />
            </div>
            <span className="text-xs font-semibold whitespace-nowrap" aria-hidden="true" style={{ color: 'var(--color-theme-text-secondary)', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
              {current}/{next}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      {/* Coins */}
      {showRewards && VIRTUAL_REWARD_ECONOMY_ENABLED && (
        <div
          className="flex items-center gap-1 px-2 py-1 rounded-full"
          style={{
            backgroundColor: 'var(--color-theme-secondary-light, #fef9c3)',
            color: 'var(--color-theme-secondary, #ca8a04)',
          }}
        >
          <Coins className="w-3.5 h-3.5" />
          <span className="text-xs font-bold">{coinBalance}</span>
        </div>
      )}

      {/* Notification Bell */}
      <NotificationBell childId={child.id} />

      {/* Streak */}
      {showStreaks && (
        <div
          className={`flex items-center gap-1 px-2 py-1 rounded-full ${
            child.currentStreak === 0
              ? 'opacity-40'
              : child.currentStreak >= 7
                ? 'animate-streak-glow'
                : child.currentStreak >= 3
                  ? 'animate-streak-pulse'
                  : ''
          }`}
          style={{
            backgroundColor: child.currentStreak === 0 ? 'rgba(156,163,175,0.15)' : 'rgba(239,68,68,0.1)',
          }}
        >
          <span className={`${child.currentStreak >= 7 ? 'text-lg' : 'text-base'} leading-none`}>
            {child.currentStreak >= 7 ? '🔥✨' : '🔥'}
          </span>
          <span className={`text-sm font-bold ${child.currentStreak === 0 ? 'text-gray-400' : 'text-red-600'}`}>
            {child.currentStreak}
          </span>
        </div>
      )}

      {/* User menu */}
      {user && (
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            style={{
              backgroundColor: 'var(--color-theme-surface-bg, rgba(99,102,241,0.15))',
              color: 'var(--color-theme-text-secondary, #6b7280)',
            }}
            aria-label="Account menu"
          >
            <User className="w-4 h-4" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <p className="text-sm font-bold text-gray-800 dark:text-white truncate">
                  {profile?.display_name || user.email?.split('@')[0]}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
              </div>
              <div className="py-1">
                {showBilling && (
                  <a
                    href="/pricing"
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    Manage Plan
                  </a>
                )}
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    signOut();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
